import { Button, message, Tabs, Empty } from 'antd';
import { TabTitle } from './TabTitle';
import { css } from '@emotion/css';
import { TicketDetail } from './TicketDetail';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTicketTask } from 'hooks/business/chat/useTicket';
import { NewTicket } from './NewTicket';
import { useTranslate } from '@tolgee/react';
import { PlusOutlined } from '@ant-design/icons';

interface TicketProps {
  conversation: Conversation.Conversation;
  ticketList: Todo.TodoInstance[];
  isLoading: boolean;
  appendTicketToList?: (ticket: Todo.TodoInstance) => void;
}

export const Ticket = (props: TicketProps) => {
  const { ticketList, conversation, appendTicketToList } = props;
  const { conversationId, storeId, buyerId, externalBuyerId, channel } = conversation;
  const { t } = useTranslate();
  const [activeTicket, setActiveTicket] = useState<string | null>(ticketList?.[0]?.id ?? null);
  const [isNewTicket, setIsNewTicket] = useState<boolean>(false);

  const { task, policies, typeSource } = useTicketTask(storeId);
  const scrollTopCache = useRef<Record<string, number>>({});

  const handleSaveScrollTop = (ticketId: string, scrollTop: number) => {
    scrollTopCache.current[ticketId] = scrollTop;
  };

  const listParams =
    storeId && buyerId && externalBuyerId
      ? { storeId, buyerId, tradeBuyerId: externalBuyerId }
      : null;

  const handleChange = (key: string) => {
    setIsNewTicket(false);
    setActiveTicket(key);
  };

  const toNewTicket = () => {
    if (!task) {
      message.warn(t('no_task_tip'));
      return;
    }
    setActiveTicket(null);
    setIsNewTicket(true);
  };

  const activeTicketInstance = useMemo(
    () => ticketList.find((ticket) => ticket.id === activeTicket),
    [ticketList, activeTicket],
  );

  useEffect(() => {
    if (!isNewTicket && !activeTicketInstance) {
      setActiveTicket(ticketList?.[0]?.id);
    }
  }, [ticketList, activeTicketInstance, isNewTicket]);

  const isEmpty = ticketList.length === 0 && !isNewTicket;

  if (isEmpty || !task) {
    return (
      <div className="pt-14 flex flex-col items-center justify-center">
        <Empty
          className="!mb-3"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<div>{t('todo.暂无待办')}</div>}
        />
        <div>
          <Button icon={<PlusOutlined />} type="primary" onClick={toNewTicket}>
            {t('todo.新建')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-3 overflow-hidden flex flex-col h-full">
      <div className="flex items-center">
        <Tabs
          activeKey={activeTicket || ''}
          type="card"
          className={tabsCls}
          onChange={handleChange}
        >
          {ticketList.map((ticket) => {
            const { type, typeName, subType, subType2, subType3 } = ticket;
            return (
              <Tabs.TabPane
                closable={false}
                tab={
                  <TabTitle
                    type={type}
                    typeName={typeName}
                    subType={subType}
                    subType2={subType2}
                    subType3={subType3}
                  />
                }
                key={ticket.id}
              />
            );
          })}
        </Tabs>
        <button
          type="button"
          className={`${createTicketBtnCls} ${isNewTicket ? 'active' : ''}`}
          onClick={toNewTicket}
        >
          <PlusOutlined />
        </button>
      </div>
      {isNewTicket && !!task ? (
        <NewTicket
          platform={channel as General.PlatformUpperCase}
          typeSource={typeSource}
          storeId={storeId}
          conversationId={conversationId}
          externalBuyerId={externalBuyerId}
          buyerId={buyerId}
          taskId={task.id}
          onCancel={() => {
            setIsNewTicket(false);
            if (ticketList.length > 0) {
              setActiveTicket(ticketList?.[0]?.id ?? null);
            } else {
              setActiveTicket(null);
            }
          }}
          onSuccess={(newTicket) => {
            if (newTicket) {
              appendTicketToList?.(newTicket);
              requestIdleCallback(() => {
                setActiveTicket(newTicket.id);
              });
            } else {
              setActiveTicket(ticketList?.[0]?.id ?? null);
            }
            setIsNewTicket(false);
          }}
        />
      ) : (
        activeTicketInstance &&
        !!task && (
          <TicketDetail
            ticket={activeTicketInstance}
            task={task}
            policies={policies}
            typeSource={typeSource}
            listParams={listParams}
            platform={channel as General.PlatformUpperCase}
            externalBuyerId={externalBuyerId}
            conversationId={conversation.conversationId}
            cachedScrollTop={scrollTopCache.current[activeTicketInstance.id] ?? 0}
            onSaveScrollTop={handleSaveScrollTop}
          />
        )
      )}
    </div>
  );
};

const tabsCls = css`
  .ant-tabs-nav {
    margin-bottom: 0;
    padding-left: 12px;
    padding-right: 0;
  }

  .ant-tabs-extra-content {
    display: flex;
    align-items: center;
  }

  .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
    color: var(--fett-text-primary);
    font-weight: 500;
  }

  .ant-tabs-nav-add:hover,
  .ant-tabs-tab:not(.ant-tabs-tab-active):hover {
    color: var(--fett-text-primary) !important;
    font-weight: 500;
    background-color: var(--fett-streak-grey);
  }
`;

const createTicketBtnCls = css`
  display: inline-flex;
  align-items: center;
  border: 1px solid var(--fett-border-default);
  padding: 8px 16px;
  cursor: pointer;
  height: 40px;
  border-radius: 2px;
  background-color: var(--fett-bg-subtle);
  margin-right: 12px;

  &:hover {
    color: var(--fett-text-primary) !important;
    font-weight: 500;
    background-color: var(--fett-streak-grey);
  }

  &.active {
    color: var(--fett-text-primary);
    font-weight: 500;
    background-color: var(--fett-bg-elevated);
    border-bottom: none;
  }
`;
